import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { WidgetWrapper } from '@/src/components/projects/ProjectDashboard/WidgetWrapper.jsx';
import { WidgetInner } from '@/src/components/projects/ProjectDashboard/WidgetInner.jsx';
import { WidgetCard } from '@/src/components/projects/ProjectDashboard/WidgetCard.jsx';
import { WidgetsStatusBadge } from '@/src/components/projects/ProjectDashboard/WidgetsStatusBadge.jsx';
import SentimentDot from '@/src/components/SentimentDot.jsx';
import Empty from '@/src/components/Empty.jsx';
import { feedbackIntelligenceWidgetContent } from '@/src/components/EmptyStates.jsx';
import { buildPath } from '@/src/components/Link.jsx';
import { PROJECTS_INITIATIVES } from '@/src/constants/routes.js';
import { ChangeInsightsStudioLink } from '@/src/components/links/index.jsx';
import { INITIATIVE_TYPES } from '@/src/lib/schema.js';
import DatesProgressBar from '@/src/components/projects/DatesProgressBar.jsx';

export const FeedbackIntelligenceWidget = ({ data }) => {
  const navigate = useNavigate();
  const { slug, projectId } = useParams();

  const handleNavigateToInitiatives = () => {
    const path = buildPath(PROJECTS_INITIATIVES, { slug, projectId });
    navigate(path);
  };

  const { total, forms } = data;
  const hasData = forms && forms.length > 0;

  return (
    <WidgetWrapper className="feedback-intelligence-widget">
      <div className="flex align-center m-b-12">
        <h2>Feedback Intelligence</h2>
        <strong className="header-item bordered component-bg-white text-primary">
          <span className="text-muted">Total: </span>
          <span>{total}</span>
        </strong>
      </div>
      {hasData ? (
        <WidgetInner height={630} className="flex flex-col flex-align-start">
          {forms.map((form) => {
            const initiativeType = INITIATIVE_TYPES.find((type) => form[type]);
            return (
              <WidgetCard
                key={form.id}
                className="flex flex-col p-x-12 p-y-10 m-b-16 w-100"
              >
                <div className="flex flex-justify align-center m-b-4">
                  <ChangeInsightsStudioLink
                    initiative={{ id: form.initiativeId, type: initiativeType }}
                  >
                    <span className="font-size-18 font-semibold">
                      {form.title}
                    </span>
                  </ChangeInsightsStudioLink>
                  <WidgetsStatusBadge sentiment={form.status} />
                </div>
                {initiativeType && (
                  <div className="flex font-size-12 m-b-20">
                    <div className="text-muted m-r-4">
                      {initiativeType.charAt(0).toUpperCase() +
                        initiativeType.slice(1)}
                      :
                    </div>
                    <div className="flex align-center">
                      {form[initiativeType]}
                    </div>
                  </div>
                )}
                <div className="flex m-b-20">
                  <div className="flex-1 font-size-12">
                    <div className="flex flex-col flex-align-start">
                      <span className="text-muted m-b-8">Dates</span>
                      <span className="font-semibold">
                        {moment(form.startDate).format('MM/DD/YYYY')} -{' '}
                        {moment(form.endDate).format('MM/DD/YYYY')}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 border-left border-right">
                    <div className="flex flex-col flex-align-center">
                      <div className="flex flex-col flex-align-start">
                        <span className="text-muted font-size-12 m-b-4">
                          Impact
                        </span>
                        <span className="font-semibold">
                          <SentimentDot sentiment={form.impact} reverse />
                          {form.impact.charAt(0).toUpperCase() +
                            form.impact.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 font-size-12">
                    <div className="flex flex-col flex-align-center">
                      <div className="flex flex-col flex-align-start">
                        <span className="text-muted m-b-8">Completion</span>
                        <span className="font-semibold">
                          {form.completionPercentage}% ({form.completionCount}/
                          {form.completionTotal})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <DatesProgressBar
                  startDate={new Date(form.startDate)}
                  endDate={new Date(form.endDate)}
                />
              </WidgetCard>
            );
          })}
        </WidgetInner>
      ) : (
        <WidgetInner height={630} className="flex flex-col">
          <Empty
            simplified
            content={feedbackIntelligenceWidgetContent}
            action={handleNavigateToInitiatives}
          />
        </WidgetInner>
      )}
    </WidgetWrapper>
  );
};
